// Multiselect field - mode-aware
// Edit/Create: vertical checkbox list (like RadioField but with square checkboxes)
// View/Admin-view: shows all options with selected state indicated
// singleSelect: array-backed field with only one valid choice (OPA-307: business model type) —
// same tag-trigger dropdown language as RadioField, but writes/reads a 1-item array instead of
// a scalar, so the backend's array schema doesn't need to change.
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FieldError, FieldWrapper } from '@startus/ui';
import { useFormMode } from '../../hooks/useFormMode';
import type { BaseFieldProps } from '../../types/fields';

export function MultiselectField({ name, options = [], singleSelect = false }: BaseFieldProps) {
  const { t } = useTranslation('common');
  const { isReadOnly } = useFormMode();
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const value = watch(name);
  const error = errors[name]?.message as string | undefined;
  const valueArray = Array.isArray(value) ? value : [];

  if (singleSelect) {
    const selectedOption = options.find((opt) => valueArray.includes(opt.value));

    // Read-only: selected value as a single pill, same as RadioField (not all options)
    if (isReadOnly) {
      return (
        <FieldWrapper>
          {selectedOption ? (
            <span
              title={selectedOption.label}
              className="inline-flex max-w-[220px] items-center truncate rounded-full bg-primary/15 px-3 py-1.5 text-sm font-medium text-primary mt-2"
            >
              {selectedOption.label}
            </span>
          ) : (
            <span className="text-sm text-gray-500 mt-2 inline-block">
              {t('common.clickToEnter')}
            </span>
          )}
        </FieldWrapper>
      );
    }

    // Editable: single-select dropdown, same tag-trigger + menu language as RadioField
    return (
      <FieldWrapper>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <div className="dropdown mt-2">
              <label
                tabIndex={0}
                title={selectedOption ? selectedOption.label : undefined}
                className="inline-flex max-w-[220px] items-center gap-1.5 cursor-pointer rounded-full bg-primary/15 px-3 py-1.5 text-sm font-medium text-primary"
              >
                <span className="truncate">
                  {selectedOption ? selectedOption.label : t('common.select')}
                </span>
                <svg
                  className="w-3.5 h-3.5 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </label>
              <ul
                tabIndex={0}
                className="dropdown-content z-[999] mt-1 py-1 bg-[#2a2a2a] rounded-lg shadow-lg border border-white/10 min-w-40"
              >
                {options.map((opt) => (
                  <li key={opt.value}>
                    <button
                      type="button"
                      onClick={() => {
                        field.onChange([opt.value]);
                        (document.activeElement as HTMLElement)?.blur();
                      }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-white/10 transition-colors ${
                        valueArray.includes(opt.value) ? 'bg-white/10 text-white' : 'text-gray-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        />
        <FieldError message={error} />
      </FieldWrapper>
    );
  }

  // Read-only: all options as pills, selected ones filled
  if (isReadOnly) {
    return (
      <FieldWrapper>
        {/*<Label required={required} fieldNumber={fieldNumber}>{label}</Label>*/}
        <div className="flex flex-wrap gap-2 mt-2">
          {options.map((opt) => {
            const isSelected = valueArray.includes(opt.value);
            return (
              <span
                key={opt.value}
                title={opt.label}
                className={`max-w-[220px] truncate px-3 py-1.5 rounded-full text-sm font-medium ${
                  isSelected ? 'bg-primary text-primary-content' : 'bg-white/5 text-gray-400'
                }`}
              >
                {opt.label}
              </span>
            );
          })}
        </div>
      </FieldWrapper>
    );
  }

  // Editable: horizontal pill toggle buttons (multi-select)
  return (
    <FieldWrapper>
      {/*<Label required={required} fieldNumber={fieldNumber}>{label}</Label>*/}
      <Controller
        name={name}
        control={control}
        defaultValue={[]}
        render={({ field }) => {
          const currentValues = Array.isArray(field.value) ? field.value : [];
          return (
            <div className="flex flex-wrap gap-2 mt-2">
              {options.map((opt) => {
                const isSelected = currentValues.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        field.onChange(currentValues.filter((v: string) => v !== opt.value));
                      } else {
                        field.onChange([...currentValues, opt.value]);
                      }
                    }}
                    title={opt.label}
                    className={`max-w-[220px] truncate px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-primary text-primary-content'
                        : 'bg-white/5 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          );
        }}
      />
      <FieldError message={error} />
    </FieldWrapper>
  );
}
